import { useEffect, useState } from 'react'
import { useChatStore } from '@/store/chatStore'
import { useWidgetConfig } from '@/hooks/useWidgetConfig'
import { widgetLogin, initApiBaseUrl, setAuthToken, getSuggestedQuestions, getWidgetSettings } from '@/services/api'
import { checkHasSchedule } from '@/services/appointmentApi'
import { ThemeProvider } from '@/components/shared/ThemeProvider'
import { ChatPanel } from '@/components/panel/ChatPanel'
import { ChatBubble } from '@/components/bubble/ChatBubble'

// ─────────────────────────────────────────────────────────────────────────────
// EmbedLayout
// Owns the postMessage to loader.js so it always fires on isPanelOpen change
// regardless of whether ChatBubble is mounted or not.
// ─────────────────────────────────────────────────────────────────────────────
function EmbedLayout({ isPanelOpen, closePanel }: { isPanelOpen: boolean; closePanel: () => void }) {
  // Always fires — not dependent on ChatBubble being mounted
  useEffect(() => {
    window.parent.postMessage(
      { type: 'dirayah:panel', open: isPanelOpen },
      '*'
    )
  }, [isPanelOpen])

  return (
    <div className="w-full h-full relative">
      {isPanelOpen && (
        <div className="absolute bottom-20 right-4 w-[400px] h-[480px]">
          <ChatPanel onClose={closePanel} />
        </div>
      )}
     <div style={{ display: isPanelOpen ? 'none' : 'block' }}>
  <ChatBubble />
</div>
    </div>
  )
}

export default function App() {
  const config      = useWidgetConfig()
  const setConfig   = useChatStore((s) => s.setConfig)
  const isPanelOpen = useChatStore((s) => s.isPanelOpen)
  const closePanel  = useChatStore((s) => s.closePanel)

  // Widget does not render ChatPanel until auth is complete
  // Prevents SignalR from connecting without a valid JWT
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authError, setAuthError]             = useState(false)

  const params  = new URLSearchParams(window.location.search)
  const mode    = params.get('mode') || 'standalone'
  const isEmbed = mode === 'embed'

  useEffect(() => {
    setConfig(config)
    initApiBaseUrl(config.apiUrl)

    if (config.widgetApiKey && config.domainId) {
      widgetLogin(config.widgetApiKey, config.domainId)
        .then(({ token, tenantId }) => {
          setAuthToken(token)
          useChatStore.getState().setAuthToken(token)
          useChatStore.getState().setTenantId(tenantId)
          setIsAuthenticated(true)
          console.info('[Widget] Authenticated successfully')
          // Pre-fetch suggested questions and appointment schedule availability
          getSuggestedQuestions(config.domainId)
            .then((qs) => { if (qs.length > 0) useChatStore.getState().setSuggestedQuestions(qs) })
            .catch(() => {})
          checkHasSchedule(config.domainId)
            .then((has) => { if (has) useChatStore.getState().setHasAppointmentSchedule(true) })
            .catch(() => {})
          // Which action buttons this domain wants — currently the WhatsApp handoff.
          getWidgetSettings(config.domainId)
            .then((s) => {
              if (s.showWhatsAppButton && s.whatsAppNumber)
                useChatStore.getState().setWhatsAppNumber(s.whatsAppNumber)
            })
            .catch(() => {})
        })
        .catch((err) => {
          console.error('[Widget] Authentication failed:', err)
          setAuthError(true)
        })
    } else {
      setIsAuthenticated(true)
    }
  }, [])

  if (authError) {
    return (
      <ThemeProvider>
        <div className="w-full h-full flex items-center justify-center">
          <p className="text-sm text-red-500">
            Widget configuration error. Please check your API key.
          </p>
        </div>
      </ThemeProvider>
    )
  }

  if (!isAuthenticated) return null

  return (
    <ThemeProvider>
      {isEmbed ? (
        <EmbedLayout isPanelOpen={isPanelOpen} closePanel={closePanel} />
      ) : (
        <div className="
          w-full h-full flex items-center justify-center
          bg-[var(--color-surface)] p-4
        ">
          <div className="w-full max-w-2xl h-full max-h-[800px]">
            <ChatPanel />
          </div>
        </div>
      )}
    </ThemeProvider>
  )
}